"use client";

import { Link, Navigate, Outlet, useLocation } from "react-router-dom";

import { Card } from "@/components/ui-system";
import { cn } from "@/lib/utils/cn";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

const SETTINGS_TABS = [
  { href: "/settings/entitlements", moduleKey: "entitlements", label: "Entitlements" },
  { href: "/settings/activity-policy", moduleKey: "activity_policy", label: "Logger activity" },
] as const;

function useAllowedSettingsTabs() {
  const user = useAuthStore((s) => s.user);
  const permissions = useEffectivePermissions();
  const tabs = SETTINGS_TABS.filter((tab) => {
    return hasModulePermission(user, permissions.permissionMap, tab.moduleKey, "view");
  });

  return { user, permissions, tabs };
}

export function SettingsIndexRedirect() {
  const { user, permissions, tabs } = useAllowedSettingsTabs();

  if (!user) {
    return null;
  }
  if (permissions.isLoading && user.role !== "super_admin") {
    return <div className="p-8 text-sm text-muted">Checking access...</div>;
  }

  return <Navigate to={tabs[0]?.href ?? "/overview"} replace />;
}

export function SettingsPage() {
  const { pathname } = useLocation();
  const { user, permissions, tabs } = useAllowedSettingsTabs();

  if (!user) {
    return null;
  }
  if (permissions.isLoading && user.role !== "super_admin") {
    return <div className="p-8 text-sm text-muted">Checking access...</div>;
  }

  if (tabs.length === 0) {
    return (
      <div className="mx-auto w-full max-w-workspace px-ds-3 pb-ds-8 pt-ds-3 sm:px-ds-4 md:pt-ds-4 lg:px-ds-5">
        <Card className="p-ds-5 text-caption text-muted">You do not have access to settings.</Card>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto w-full max-w-workspace px-ds-3 pt-ds-3 sm:px-ds-4 md:pt-ds-4 lg:px-ds-5">
        <div className="flex flex-wrap items-end justify-between gap-ds-3">
          <div>
            <p className="text-title font-semibold text-foreground">Settings</p>
            <p className="mt-ds-1 text-caption text-muted">Workspace policies and logger behavior.</p>
          </div>
          <div className="segment-tabs">
            {tabs.map((tab) => {
              const active = pathname === tab.href || pathname.startsWith(`${tab.href}/`);
              return (
                <Link
                  key={tab.href}
                  to={tab.href}
                  className={cn("segment-tab", active && "segment-tab-active")}
                  aria-current={active ? "page" : undefined}
                >
                  {tab.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      <Outlet />
    </>
  );
}
